import { Button, Card, Col, Form, InputNumber, Layout, message, Row } from 'antd';
import InvoiceDetailsDisplay from '../invoice_page_display/InvoiceDetailsDisplay';
import { applyMarkupToInvoice, getSanitisedInvoice } from '../../utils/WorkOrderUtils';
import SupplierInvoiceForm from '../supplier_invoice_form/SupplierInvoiceForm';
import InvoicePageDisplay from '../invoice_page_display/InvoicePageDisplay';
import { getCurrency, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import React, { useCallback, useEffect, useState } from 'react';
import {
	appendWorkOrderNotesForSupplier,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';
import { markUserNotificationsReadForSupplier } from '../../thunks/user_notifications_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	createInvoiceSubContractor,
	getContractorInvoices,
	getOtherContractorInvoices,
	getOtherInvoices,
	invoicesRestCrudThunksForSupplier,
	updateInvoiceSubContractor,
} from '../../thunks/invoices_thunks';
import { isBuyerOrInternalTech } from '../../utils/AuthUtils';
import { getInvoiceSubUrl } from '../invoice_utilities/InvoiceUtilities';

const { Content } = Layout;

const SupplierInvoiceDetailDetailsPageWrapper = ({
	invoice = {},
	refreshInvoice,
	workOrder,
	showViewInvoicePage,
	// Off by default; the standalone detail page and the work order's invoices tab opt in. The tab
	// passes `invoice.workOrder` already merged with the live work order, which the banner relies on.
	showEnrichmentBanner = false,

	createContractorInvoice,
	updateContractorInvoice,
	createInvoice,
	updateInvoice,

	history,
	currentUser,
	isClientView,
	userType,

	getOtherInvoices,
	getOtherContractorInvoices,
}) => {
	const [addingMarkup, setAddingMarkup] = useState(false);
	const [editingInvoice, setEditingInvoice] = useState(false);
	const [copyInvoice, setCopyInvoice] = useState({});
	const [contractorInvoice, setContractorInvoice] = useState(undefined);
	const [fetchingContractorInvoice, setFetchingContractorInvoice] = useState(false);
	const [loading, setLoading] = useState(false);

	const currency = getCurrency({ workOrder: workOrder });
	const { subContractedFromWorkOrder } = workOrder;
	const redirectUrl = useCallback(
		(newInvoice) =>
			history.location.pathname.includes('invoices/subcontractor')
				? `/${getInvoiceSubUrl(userType, currentUser, newInvoice)}/detail/${nullSafeGet(
						'id',
						newInvoice
				  )}/details`
				: `/supplier/workOrders/detail/${nullSafeGet('workOrder.id', invoice)}/client_invoices`,

		[invoice, userType, currentUser]
	);

	useEffect(() => {
		if (!isBuyerOrInternalTech(currentUser) && !isClientView && !fetchingContractorInvoice) {
			const params = subContractedFromWorkOrder
				? {
						contractorWorkOrderId: nullSafeGet('id', subContractedFromWorkOrder),
						contractorSupplierFacilityId: nullSafeGet(
							'contractorSupplierFacilityId',
							subContractedFromWorkOrder
						),
				  }
				: {
						workOrderId: nullSafeGet('subContractedToWorkOrder.workOrderId', workOrder),
						supplierFacilityId: nullSafeGet(
							'subContractedToWorkOrder.contractorSupplierFacilityId',
							workOrder
						),
				  };
			const fn = subContractedFromWorkOrder ? getOtherContractorInvoices : getOtherInvoices;

			setFetchingContractorInvoice(true);
			fn(params).then((otherInvoices) => {
				if (nullSafeGetOrElse('length', otherInvoices, 0) > 0) {
					setContractorInvoice(otherInvoices[0]);
				}
				setFetchingContractorInvoice(false);
			});
		}
	}, [workOrder, isClientView, currentUser]);

	const copyCreateInvoice = useCallback(() => {
		setLoading(true);
		if (subContractedFromWorkOrder) {
			const fn = contractorInvoice ? updateContractorInvoice : createContractorInvoice;
			fn({
				...copyInvoice,
				id: contractorInvoice ? nullSafeGet('id', contractorInvoice) : undefined,
				status: 'draft',
				workOrderId: undefined,
				supplierFacilityId: undefined,
				contractorWorkOrderId: subContractedFromWorkOrder.id,
				contractorSupplierFacilityId: subContractedFromWorkOrder.contractorSupplierFacilityId,
				subSupplierFacilityId: subContractedFromWorkOrder.subSupplierFacilityId,
				invoicePDFLink: undefined,
				...(addingMarkup
					? {
							scope: undefined,
							invoiceNumber: undefined,
							externalWorkOrderId: undefined,
							dateOfInvoice: undefined,
							paymentTerms: undefined,
							dueDate: undefined,
					  }
					: {}),
			})
				.then((record) => {
					message.success('Invoice copied successfully');
					refreshInvoice();
					setAddingMarkup(false);
					setEditingInvoice(false);
					history.push(redirectUrl(record));
				})
				.catch((err) => {
					if (err) {
						if (
							err.startsWith(`Invoice already exists for this Work order`) ||
							err.startsWith(`You can't submit an Invoice for more`)
						) {
							message.error(err, 10);
						} else {
							message.error('An error occurred when submitting this invoice.');
						}
					}
				})
				.finally(() => {
					setLoading(false);
				});
		} else {
			const fn = contractorInvoice ? updateInvoice : createInvoice;
			fn({
				...copyInvoice,
				id: contractorInvoice ? nullSafeGet('id', contractorInvoice) : undefined,
				status: 'draft',
				workOrderId: workOrder.id,
				supplierFacilityId: nullSafeGet('facility.id', currentUser),
				contractorWorkOrderId: undefined,
				contractorSupplierFacilityId: undefined,
				subSupplierFacilityId: undefined,
				invoicePDFLink: undefined,
				...(addingMarkup
					? {
							scope: undefined,
							invoiceNumber: undefined,
							externalWorkOrderId: undefined,
							dateOfInvoice: undefined,
							paymentTerms: undefined,
							dueDate: undefined,
					  }
					: {}),
			})
				.then((record) => {
					message.success('Invoice copied successfully');
					refreshInvoice();
					setAddingMarkup(false);
					setEditingInvoice(false);
					history.push(redirectUrl(record));
				})
				.catch((err) => {
					if (err) {
						if (
							err.startsWith(`Invoice already exists for this Work order`) ||
							err.startsWith(`You can't submit an Invoice for more`)
						) {
							message.error(err, 10);
						} else {
							message.error('An error occurred when submitting this invoice.');
						}
					}
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [
		createContractorInvoice,
		createInvoice,
		subContractedFromWorkOrder,
		copyInvoice,
		history,
		workOrder,
		addingMarkup,
	]);

	return (
		<>
			{addingMarkup ? (
				<>
					<div className=" flex flex-row justify-end">
						<Button style={{ marginRight: 12 }} onClick={() => setAddingMarkup(false)}>
							Cancel
						</Button>
					</div>

					<Row gutter={12} style={{ margin: '16px 8px ' }}>
						<Col span={12}>
							<Card>
								<div className=" flex w-full flex-row items-baseline justify-between">
									<h5>Subcontractor Invoice</h5>
								</div>

								<InvoiceDetailsDisplay currency={currency} invoice={invoice} />
							</Card>
						</Col>
						<Col span={12}>
							<Card>
								<div className=" flex w-full flex-row items-baseline justify-between">
									<h5>Your Invoice</h5>
									<div>
										<Form.Item style={{ marginBottom: 0 }} label="Add Markup">
											<InputNumber
												value={copyInvoice.markup}
												addonBefore="+"
												addonAfter="%"
												onChange={(value) => {
													setCopyInvoice({
														...invoice,
														...applyMarkupToInvoice(invoice, value),
													});
												}}
											/>
										</Form.Item>
									</div>
								</div>

								<InvoiceDetailsDisplay currency={currency} invoice={copyInvoice} />
								<div className="flex flex-row justify-end">
									<Button
										loading={loading}
										type="primary"
										className="mt-4"
										onClick={copyCreateInvoice}
									>
										{contractorInvoice ? 'Update' : 'Create'} Invoice
									</Button>
								</div>
							</Card>
						</Col>
					</Row>
				</>
			) : editingInvoice ? (
				<>
					<div className=" mr-4 flex flex-row justify-end">
						<Button style={{ marginRight: 12 }} onClick={() => setEditingInvoice(false)}>
							Cancel
						</Button>
					</div>
					<Row gutter={12} style={{ margin: '16px 8px ' }}>
						<Card style={{ margin: '16px' }}>
							<SupplierInvoiceForm
								formData={{
									...invoice,
									id: contractorInvoice ? contractorInvoice.id : undefined,
									workOrderId: workOrder.id,
									scope: undefined,
									invoiceNumber: undefined,
									externalWorkOrderId: undefined,
									dateOfInvoice: undefined,
									paymentTerms: undefined,
									dueDate: undefined,
								}}
								onSuccess={(record) => {
									message.success('Invoice copied successfully');
									history.push(redirectUrl(record));
								}}
								copyMode
							/>
						</Card>
					</Row>
				</>
			) : (
				<>
					<InvoicePageDisplay
						invoice={getSanitisedInvoice(invoice)}
						refreshInvoice={refreshInvoice}
						showViewInvoicePage={showViewInvoicePage}
						isClientView={isClientView}
						workOrder={workOrder}
						setAddingMarkup={setAddingMarkup}
						setEditingInvoice={setEditingInvoice}
						setCopyInvoice={setCopyInvoice}
						otherInvoice={contractorInvoice}
						otherInvoiceLoading={fetchingContractorInvoice}
						displayComparison={true}
						showEnrichmentBanner={showEnrichmentBanner}
					/>
				</>
			)}
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	location: ownProps.location,
});

const mapDispatchToProps = (dispatch) => ({
	appendWorkOrderNotes: (entity) => dispatch(appendWorkOrderNotesForSupplier(entity)),
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	markUserNotificationsRead: (entityId) =>
		dispatch(markUserNotificationsReadForSupplier('Quote', entityId)),
	getContractorInvoices: (params, targetCollectionName) =>
		dispatch(getContractorInvoices(params, targetCollectionName)),
	createContractorInvoice: (invoice) => dispatch(createInvoiceSubContractor(invoice)),
	updateContractorInvoice: (entity) => dispatch(updateInvoiceSubContractor(entity)),
	createInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.create(invoice)),
	updateInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.update(invoice)),
	getOtherContractorInvoices: (params) => dispatch(getOtherContractorInvoices(params)),
	getOtherInvoices: (params) => dispatch(getOtherInvoices(params)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierInvoiceDetailDetailsPageWrapper)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
